import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../context/RoleContext';
import { useLanguage } from '../context/LanguageContext';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy, Timestamp } from 'firebase/firestore';
import { Customer, getCustomers, linkUserToCustomer, unlinkUserFromCustomer, updateUserProfile, updateUserPassword, disableUser, enableUser } from '../firebase/services';

// Composants
import UserCreationForm from '../components/admin/UserCreationForm';
import CustomerCreationForm from '../components/admin/CustomerCreationForm';
import UsersList from '../components/admin/UsersList';
import CustomersList from '../components/admin/CustomersList';

// Interface pour les utilisateurs
interface User {
  id: string;
  email: string;
  displayName: string;
  jobTitle?: string;
  avatarUrl?: string;
  roleId: number;
  customerId?: string;
  isDisabled?: boolean;
  createdAt: Date;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const ControlAccessAdmin: React.FC = () => {
  const { currentUser } = useAuth();
  const { isAdmin, loading: roleLoading } = useRole();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  // État pour les onglets
  const [tabValue, setTabValue] = useState(0);
  
  // États pour la gestion des utilisateurs et clients
  const [users, setUsers] = useState<User[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  
  // États pour les notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Helper function to get string value from translation
  const getTranslationString = (key: string, defaultValue: string = ''): string => {
    const translation = t(key);
    return typeof translation === 'string' ? translation : defaultValue;
  };

  // Redirect to client dashboard if not admin
  useEffect(() => {
    if (!roleLoading && !isAdmin) {
      navigate('/dashboard/admin');
    }
  }, [isAdmin, roleLoading, navigate]);

  // Charger la liste des utilisateurs et des clients
  useEffect(() => {
    const fetchData = async () => {
      if (!isAdmin) return;
      
      try {
        setLoading(true);
        
        // Charger les utilisateurs
        const usersCollection = collection(db, 'users');
        const usersQuery = query(usersCollection, orderBy('createdAt', 'desc'));
        const usersSnapshot = await getDocs(usersQuery);
        
        const usersData: User[] = [];
        usersSnapshot.forEach((doc) => {
          const userData = doc.data();
          const createdAtTimestamp = userData.createdAt as Timestamp;
          usersData.push({
            id: doc.id,
            ...userData,
            createdAt: createdAtTimestamp.toDate()
          } as User);
        });
        
        setUsers(usersData);
        
        // Charger les clients
        const customersData = await getCustomers();
        setCustomers(customersData);
      } catch (error) {
        console.error('Error fetching data:', error);
        showNotification(getTranslationString('common.error.loading', 'Erreur lors du chargement des données'), 'error');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [isAdmin]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Afficher une notification
  const showNotification = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Fermer la notification
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Gérer la création d'un utilisateur
  const handleUserCreated = (userId: string, userData: any) => {
    setUsers(prevUsers => [{
      id: userId,
      ...userData,
      createdAt: userData.createdAt
    }, ...prevUsers]);
    
    showNotification(getTranslationString('controlAccess.notification.userCreated', 'Utilisateur créé avec succès'), 'success');
  };

  // Gérer la création d'un client
  const handleCustomerCreated = (customerId: string, customerData: any) => {
    setCustomers(prevCustomers => [{
      id: customerId,
      ...customerData,
      createdAt: customerData.createdAt
    }, ...prevCustomers]);
    
    showNotification(getTranslationString('controlAccess.notification.customerCreated', 'Client créé avec succès'), 'success');
  };

  // Gérer l'association d'un utilisateur à un client
  const handleLinkUser = async (userId: string, customerId: string) => {
    try {
      const success = await linkUserToCustomer(userId, customerId);
      
      if (success) {
        // Mettre à jour l'état local des utilisateurs
        setUsers(prevUsers => prevUsers.map(user => 
          user.id === userId ? { ...user, customerId } : user
        ));
        
        // Trouver les noms pour le message de notification
        const user = users.find(u => u.id === userId);
        const customer = customers.find(c => c.id === customerId);
        
        const message = getTranslationString('controlAccess.notification.userLinked', 'Utilisateur {userName} associé au client {customerName} avec succès')
          .replace('{userName}', user?.displayName || userId)
          .replace('{customerName}', customer?.companyName || customerId);
        
        showNotification(message, 'success');
        return true;
      } else {
        showNotification(getTranslationString('controlAccess.notification.userLinkError', "Erreur lors de l'association de l'utilisateur"), 'error');
        return false;
      }
    } catch (error) {
      console.error("Error linking user to customer:", error);
      showNotification(getTranslationString('controlAccess.notification.userLinkError', "Erreur lors de l'association de l'utilisateur"), 'error');
      return false;
    }
  };

  // Gérer la dissociation d'un utilisateur d'un client
  const handleUnlinkUser = async (userId: string) => {
    try {
      // Trouver l'utilisateur et son client actuel avant de le dissocier
      const user = users.find(u => u.id === userId);
      const customerId = user?.customerId;
      const customer = customers.find(c => c.id === customerId);
      
      const success = await unlinkUserFromCustomer(userId);
      
      if (success) {
        // Mettre à jour l'état local
        setUsers(prevUsers => prevUsers.map(user => 
          user.id === userId ? { ...user, customerId: undefined } : user
        ));
        
        const message = getTranslationString('controlAccess.notification.userUnlinked', 'Utilisateur {userName} dissocié du client {customerName} avec succès')
          .replace('{userName}', user?.displayName || userId)
          .replace('{customerName}', customer?.companyName || customerId || getTranslationString('controlAccess.notification.unknownCustomer', 'inconnu'));
        
        showNotification(message, 'success');
        return true;
      } else {
        showNotification(getTranslationString('controlAccess.notification.userUnlinkError', "Erreur lors de la dissociation de l'utilisateur"), 'error');
        return false;
      }
    } catch (error) {
      console.error("Error unlinking user from customer:", error);
      showNotification(getTranslationString('controlAccess.notification.userUnlinkError', "Erreur lors de la dissociation de l'utilisateur"), 'error');
      return false;
    }
  };

  // Gérer la mise à jour d'un utilisateur
  const handleUpdateUser = async (userId: string, userData: Partial<User>) => {
    try {
      // Mettre à jour le profil utilisateur dans Firestore
      await updateUserProfile(userId, {
        displayName: userData.displayName,
        email: userData.email,
        roleId: userData.roleId,
        jobTitle: userData.jobTitle,
        avatarUrl: userData.avatarUrl
      });
      
      // Mettre à jour l'état local des utilisateurs
      setUsers(prevUsers => prevUsers.map(user => 
        user.id === userId ? { ...user, ...userData } : user
      ));
      
      showNotification(getTranslationString('controlAccess.notification.userUpdated', 'Utilisateur mis à jour avec succès'), 'success');
      return true;
    } catch (error) {
      console.error("Error updating user:", error);
      showNotification(getTranslationString('controlAccess.notification.userUpdateError', "Erreur lors de la mise à jour de l'utilisateur"), 'error');
      return false;
    }
  };

  // Gérer la réinitialisation du mot de passe
  const handleResetPassword = async (email: string) => {
    try {
      // Firebase sendPasswordResetEmail n'a pas besoin d'un nouveau mot de passe
      // Passer une chaîne vide comme second argument
      const success = await updateUserPassword(email, "");
      
      if (success) {
        showNotification(getTranslationString('controlAccess.notification.passwordResetSent', 'Email de réinitialisation envoyé avec succès'), 'success');
        return true;
      } else {
        showNotification(getTranslationString('controlAccess.notification.passwordResetError', "Erreur lors de l'envoi de l'email de réinitialisation"), 'error');
        return false;
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      showNotification(getTranslationString('controlAccess.notification.passwordResetError', "Erreur lors de la réinitialisation du mot de passe"), 'error');
      return false;
    }
  };

  // Gérer le blocage d'un utilisateur
  const handleDisableUser = async (userId: string) => {
    try {
      const success = await disableUser(userId);
      
      if (success) {
        // Mettre à jour l'état local
        setUsers(prevUsers => prevUsers.map(user => 
          user.id === userId ? { ...user, isDisabled: true } : user
        ));
        
        showNotification(getTranslationString('controlAccess.notification.userDisabled', 'Utilisateur bloqué avec succès'), 'success');
        return true;
      } else {
        showNotification(getTranslationString('controlAccess.notification.userDisableError', "Erreur lors du blocage de l'utilisateur"), 'error');
        return false;
      }
    } catch (error) {
      console.error("Error disabling user:", error);
      showNotification(getTranslationString('controlAccess.notification.userDisableError', "Erreur lors du blocage de l'utilisateur"), 'error');
      return false;
    }
  };

  // Gérer le déblocage d'un utilisateur
  const handleEnableUser = async (userId: string) => {
    try {
      const success = await enableUser(userId);
      
      if (success) {
        // Mettre à jour l'état local
        setUsers(prevUsers => prevUsers.map(user => 
          user.id === userId ? { ...user, isDisabled: false } : user
        ));
        
        showNotification(getTranslationString('controlAccess.notification.userEnabled', 'Utilisateur débloqué avec succès'), 'success');
        return true;
      } else {
        showNotification(getTranslationString('controlAccess.notification.userEnableError', "Erreur lors du déblocage de l'utilisateur"), 'error');
        return false;
      }
    } catch (error) {
      console.error("Error enabling user:", error);
      showNotification(getTranslationString('controlAccess.notification.userEnableError', "Erreur lors du déblocage de l'utilisateur"), 'error');
      return false;
    }
  };

  if (roleLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography ml={2}>{getTranslationString('common.loading', 'Loading...')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 5, px: 2 }}>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 700,
              mb: 1,
              background: 'linear-gradient(90deg, #015480, #0288d1)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <AdminPanelSettingsIcon sx={{ fontSize: 32 }} />
            {getTranslationString('controlAccess.title', 'Contrôle d\'accès administrateur')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {getTranslationString('controlAccess.subtitle', 'Gérez les utilisateurs, les clients et les droits d\'accès à la plateforme.')}
          </Typography>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin tabs">
            <Tab 
              icon={<PersonAddIcon />} 
              label={getTranslationString('controlAccess.tab.createUser', 'Créer un utilisateur')}
              id="tab-0"
              aria-controls="tabpanel-0"
            />
            <Tab 
              icon={<BusinessIcon />} 
              label={getTranslationString('controlAccess.tab.createCustomer', 'Créer un client')}
              id="tab-1"
              aria-controls="tabpanel-1"
            />
            <Tab 
              icon={<GroupIcon />} 
              label={getTranslationString('controlAccess.tab.usersList', 'Liste des utilisateurs')}
              id="tab-2"
              aria-controls="tabpanel-2"
            />
            <Tab 
              icon={<FormatListBulletedIcon />} 
              label={getTranslationString('controlAccess.tab.customersList', 'Liste des clients')}
              id="tab-3"
              aria-controls="tabpanel-3"
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <UserCreationForm 
            customers={customers} 
            onUserCreated={handleUserCreated}
            onError={(message) => showNotification(message, 'error')}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <CustomerCreationForm 
            onCustomerCreated={handleCustomerCreated}
            onError={(message) => showNotification(message, 'error')}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <UsersList 
            users={users}
            customers={customers}
            loading={loading}
            onLinkUser={handleLinkUser}
            onUnlinkUser={handleUnlinkUser}
            onUpdateUser={handleUpdateUser}
            onResetPassword={handleResetPassword}
            onDisableUser={handleDisableUser}
            onEnableUser={handleEnableUser}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <CustomersList 
            customers={customers}
            users={users}
            loading={loading}
            onLinkUser={handleLinkUser}
          />
        </TabPanel>
      </Container>
      
      {/* Notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ControlAccessAdmin; 